import { useCallback, useEffect, useState } from "react";

export interface Router {
  path: string;
  navigate: (to: string, replace?: boolean) => void;
}

export function useRouter(): Router {
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    // Listen for programmatic replaceState (e.g. after saving a conversation)
    window.addEventListener("locationchange", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("locationchange", onPop);
    };
  }, []);

  const navigate = useCallback((to: string, replace = false) => {
    if (replace) {
      window.history.replaceState(null, "", to);
    } else {
      window.history.pushState(null, "", to);
    }
    setPath(to);
  }, []);

  return { path, navigate };
}

/** Standalone navigate — use outside of React components or where the hook is not available. */
export function navigate(to: string, replace = false) {
  if (replace) {
    window.history.replaceState(null, "", to);
  } else {
    window.history.pushState(null, "", to);
  }
  window.dispatchEvent(new Event("locationchange"));
}

export function matchRoute(path: string): {
  route: string;
  params: Record<string, string>;
} {
  if (path === "/") return { route: "/", params: {} };

  const convMatch = path.match(/^\/c\/([^/]+)$/);
  if (convMatch) return { route: "/c/:id", params: { id: convMatch[1]! } };

  if (path === "/recetas") return { route: "/recetas", params: {} };
  if (path === "/recetas/nueva") return { route: "/recetas/nueva", params: {} };

  const recipeEditMatch = path.match(/^\/recetas\/([^/]+)\/editar$/);
  if (recipeEditMatch) return { route: "/recetas/:id/editar", params: { id: recipeEditMatch[1]! } };

  const recipeMatch = path.match(/^\/recetas\/([^/]+)$/);
  if (recipeMatch) return { route: "/recetas/:id", params: { id: recipeMatch[1]! } };

  if (path === "/ingredientes") return { route: "/ingredientes", params: {} };

  const ingredientMatch = path.match(/^\/ingredientes\/([^/]+)$/);
  if (ingredientMatch) return { route: "/ingredientes/:id", params: { id: ingredientMatch[1]! } };

  if (path === "/plan") return { route: "/plan", params: {} };

  const planWeekMatch = path.match(/^\/plan\/(\d{4}-\d{2}-\d{2})$/);
  if (planWeekMatch) return { route: "/plan/:startDate", params: { startDate: planWeekMatch[1]! } };

  if (path === "/stock") return { route: "/stock", params: {} };

  const stockRecipeMatch = path.match(/^\/stock\/([^/]+)$/);
  if (stockRecipeMatch)
    return { route: "/stock/:recipeId", params: { recipeId: stockRecipeMatch[1]! } };

  if (path === "/compras") return { route: "/compras", params: {} };

  if (path === "/sesiones") return { route: "/sesiones", params: {} };

  const sessionMatch = path.match(/^\/sesiones\/([^/]+)$/);
  if (sessionMatch) return { route: "/sesiones/:id", params: { id: sessionMatch[1]! } };

  return { route: path, params: {} };
}
